import './applySearchPlatePage.scss';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import toUpper from 'lodash/toUpper';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import useApiStore from '../../../store/apiStore/useApiStore';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../../store/featureStore/hooks/useFeatureFlag';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();
  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const { plate, state, fetchVehicleBy, fetchVehicleFailedMessage } =
    useApplyStore();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    delayError: 200,
    defaultValues: {
      plateValue: plate,
      stateValue: state || '',
    },
  });

  const plateValidation = {
    required: t('validation.plate.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const isPageEnabled = useFeatureFlag(FeatureFlag.ENABLE_PLATE_TO_VIN_SERVICE);

  useEffect((): void => {
    // clear any fail message state upon rendering
    useApplyStore.setState({ fetchVehicleFailedMessage: null });

    if (!isPageEnabled) {
      navigate(previous());
    }
  }, [isPageEnabled]);

  const previousStep = (): void => {
    navigate(previous());
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { plateValue, stateValue } = values;

    if (plateValue && stateValue) {
      await fetchVehicleBy({ plate: toUpper(plateValue), state: stateValue });

      if (useApplyStore.getState().fetchVehicleSuccess) {
        navigate(next());
      }
    }
  };

  return (
    <div className="apply-search-plate-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header
            progress={useWorkflowStore.getState().computed.progress}
            showDrawerToggle
            onBackClick={previousStep}
          />

          <main>
            <form
              data-testid={locator.pages.applySearchPlate.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applySearchPlate.form.h1}>
                  {t('ApplySearchPlatePage.title')}
                </h1>
                <h2 data-testid={locator.pages.applySearchPlate.form.h2}>
                  {t('ApplySearchPlatePage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <div>
                    <TextField
                      {...register('plateValue', plateValidation)}
                      error={
                        !isEmpty(errors.plateValue) ||
                        !isEmpty(fetchVehicleFailedMessage)
                      }
                      label={t('ApplySearchPlatePage.plate')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.applySearchPlate.form.plateInput,
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {(!isEmpty(errors.plateValue) ||
                      !isEmpty(fetchVehicleFailedMessage)) && (
                      <span
                        data-testid={
                          locator.pages.applySearchPlate.form.plateInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(
                          errors.plateValue?.message ||
                            fetchVehicleFailedMessage,
                        )}
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <Select
                      control={control}
                      errorTestId={
                        locator.pages.applySearchPlate.form.stateSelectError
                      }
                      inputTestId={
                        locator.pages.applySearchPlate.form.stateSelect
                      }
                      label={t('ApplySearchPlatePage.state')}
                      name="stateValue"
                      options={USStates.map((aState) => ({
                        name: aState.name,
                        value: aState.abbreviation,
                      }))}
                      rules={stateValidation}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <Button
                    color="primary"
                    data-testid={
                      locator.pages.applySearchPlate.form.submitButton
                    }
                    disabled={!isValid}
                    endIcon={<></>}
                    loading={isLoading}
                    loadingPosition="end"
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {isLoading
                      ? t('ApplySearchPlatePage.searching')
                      : t('ApplySearchPlatePage.continue')}
                  </Button>
                </div>
              </div>
              <footer data-testid={locator.pages.applySearchPlate.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplySearchPlatePage';
